import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, Tag, DollarSign, Globe, Leaf, CheckCircle } from 'lucide-react';

/**
 * ScanResultCard - Component displaying the result of a barcode scan
 * 
 * Shows:
 * - Product name and barcode
 * - Pricing information
 * - Certifications (Halal, Kosher, Organic)
 * - Country of origin
 * - API source (OpenFoodFacts, UPCitemdb, PLU, etc.)
 */
const ScanResultCard = ({
  result,
  showActions = true,
  onAddToQueue,
  onRescan,
  t = (key, fallback) => fallback
}) => {
  if (!result) return null;
  
  const {
    barcode,
    name,
    brand,
    category,
    regular_price,
    suggested_price,
    deal_price,
    discount_percent,
    is_halal,
    is_kosher,
    is_organic,
    is_vegan,
    country_code,
    country_name,
    api_source,
    confidence
  } = result;

  // Format price with currency
  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  // Get discount badge color
  const getDiscountColor = (percent) => {
    if (percent >= 70) return 'bg-red-500';
    if (percent >= 60) return 'bg-orange-500';
    return 'bg-green-500';
  };

  return (
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              {name || 'Unknown Product'}
            </CardTitle>
            {brand && (
              <p className="text-sm text-gray-500 mt-1">{brand}</p>
            )}
          </div>
          
          {discount_percent && (
            <Badge className={`${getDiscountColor(discount_percent)} text-white`}>
              {discount_percent}% OFF
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Barcode & Category */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 flex items-center gap-1">
              <Tag className="w-4 h-4" />
              Barcode
            </p>
            <p className="font-mono font-medium">{barcode}</p>
          </div>
          {category && (
            <div>
              <p className="text-gray-500">Category</p>
              <p className="font-medium">{category}</p>
            </div>
          )}
        </div>
        
        {/* Pricing */}
        <div className="grid grid-cols-3 gap-4 p-3 bg-white rounded-lg border">
          <div className="text-center">
            <p className="text-xs text-gray-500">Regular</p>
            <p className="font-medium text-gray-700 line-through">
              {formatPrice(regular_price)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Suggested</p>
            <p className="font-medium text-blue-600">
              {formatPrice(suggested_price)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Deal Price</p>
            <p className="font-bold text-green-600 text-lg">
              {formatPrice(deal_price)}
            </p>
          </div>
        </div>
        
        {/* Certifications */}
        {(is_halal || is_kosher || is_organic || is_vegan) && (
          <div className="flex flex-wrap gap-2">
            {is_halal && (
              <Badge variant="outline" className="border-green-600 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Halal
              </Badge>
            )}
            {is_kosher && (
              <Badge variant="outline" className="border-blue-600 text-blue-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Kosher
              </Badge>
            )}
            {is_organic && (
              <Badge variant="outline" className="border-emerald-600 text-emerald-700">
                <Leaf className="w-3 h-3 mr-1" />
                Organic
              </Badge>
            )}
            {is_vegan && (
              <Badge variant="outline" className="border-purple-600 text-purple-700">
                <Leaf className="w-3 h-3 mr-1" />
                Vegan
              </Badge>
            )}
          </div>
        )}
        
        {/* Country & Source */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
          {country_name && (
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {country_name} ({country_code})
            </span>
          )}
          {api_source && (
            <span>Source: {api_source}</span>
          )}
          {confidence && (
            <span>Confidence: {Math.round(confidence * 100)}%</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScanResultCard;
